import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Collapse } from "reactstrap";

type AgendaStatusLegendProps = {
  resolveStatusLabel: (status: string) => string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

const LEGEND_STATUSES = [
  "scheduled",
  "confirmed",
  "pending_deposit",
  "completed",
  "canceled",
  "no_show",
] as const;

const resolveLegendClass = (status: (typeof LEGEND_STATUSES)[number]): string => {
  switch (status) {
    case "confirmed":
    case "completed":
      return "agenda-legend-swatch--success";
    case "pending_deposit":
      return "agenda-legend-swatch--warning";
    case "canceled":
      return "agenda-legend-swatch--danger";
    case "no_show":
      return "agenda-legend-swatch--no-show";
    default:
      return "agenda-legend-swatch--primary";
  }
};

const AgendaStatusLegend = ({
  resolveStatusLabel,
  open: controlledOpen,
  onOpenChange,
}: AgendaStatusLegendProps) => {
  const { t } = useTranslation();
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const isControlled = typeof controlledOpen === "boolean";
  const open = isControlled ? controlledOpen : uncontrolledOpen;

  useEffect(() => {
    if (!open || !rootRef.current) {
      return;
    }

    rootRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [open]);

  const setOpen = (next: boolean) => {
    if (!isControlled) {
      setUncontrolledOpen(next);
    }

    onOpenChange?.(next);
  };

  return (
    <div
      ref={rootRef}
      className="agenda-status-legend mb-2"
      aria-label={t("studio.agenda.calendar.legendLabel")}
    >
      <Button
        color="link"
        size="sm"
        className="agenda-status-legend__toggle text-muted text-decoration-none px-0 mb-0"
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <i
          className={`ri-arrow-${open ? "down" : "right"}-s-line align-middle me-1`}
          aria-hidden="true"
        />
        {t("studio.agenda.calendar.legendTitle")}
        <span className="ms-1 small">({LEGEND_STATUSES.length})</span>
      </Button>
      <Collapse isOpen={open}>
        <div className="d-flex flex-wrap align-items-center gap-2 pt-1">
          {LEGEND_STATUSES.map((status) => (
            <span
              key={status}
              className="agenda-status-legend__item small d-inline-flex align-items-center gap-1"
            >
              <span
                className={`agenda-legend-swatch ${resolveLegendClass(status)}`}
                aria-hidden="true"
              />
              {resolveStatusLabel(status)}
            </span>
          ))}
        </div>
      </Collapse>
    </div>
  );
};

export default AgendaStatusLegend;
